import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { 
  Send, Loader2, Sparkles, Copy, Check, ChevronDown, Wand2, 
  FileText, Lightbulb, FileSearch, X, ExternalLink, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

// Translations
const translations = {
  nb: {
    welcomeTitle: "Hei",
    welcomeWithDoc: "Jeg ser du jobber med",
    welcomeNoDoc: "Hvordan kan jeg hjelpe deg i dag?",
    welcomeSuggestion1: "Gjennomgå dokumentet",
    welcomeSuggestion2: "Sjekk juridiske klausuler",
    welcomeSuggestion3: "Forbedre språket",
    reviewDocument: "Gjennomgå dokument",
    reviewingDocument: "Analyserer dokumentet...",
    reviewComplete: "Gjennomgang fullført",
    suggestionsFound: "forslag funnet",
    noSuggestionsFound: "Ingen forbedringsforslag funnet",
    openSection: "Åpne",
    apply: "Bruk",
    dismiss: "Avvis",
    allSuggestions: "Alle forslag",
    improvementSuggestions: "Forbedringsforslag",
    startConversation: "Start en samtale",
    askQuestion: "Still et spørsmål eller marker tekst i dokumentet for å få hjelp",
    improveText: "Forbedre tekst",
    writeSummary: "Skriv sammendrag",
    giveSuggestions: "Gi forslag",
    use: "Bruk",
    insertDirectly: "Sett inn direkte",
    withTracking: "Med endringssporing",
    asComment: "Som kommentar",
    selectedText: "Markert tekst",
    writeMessage: "Skriv meldingen din...",
    improve: "Forbedre",
    copied: "Kopiert til utklippstavle",
    selectTextFirst: "Marker tekst først",
    selectTextDescription: "Velg tekst i dokumentet du vil forbedre",
    makeMorePrecise: "Gjør denne teksten mer presis og profesjonell",
    writeSummaryPrompt: "Skriv et kort sammendrag av dokumentet",
    giveSuggestionsPrompt: "Gi forbedringsforslag til denne teksten",
    original: "Original:",
    suggested: "Foreslått:"
  },
  en: {
    welcomeTitle: "Hi",
    welcomeWithDoc: "I see you're working on",
    welcomeNoDoc: "How can I help you today?",
    welcomeSuggestion1: "Review the document",
    welcomeSuggestion2: "Check legal clauses",
    welcomeSuggestion3: "Improve the language",
    reviewDocument: "Review document",
    reviewingDocument: "Analyzing document...",
    reviewComplete: "Review complete",
    suggestionsFound: "suggestions found",
    noSuggestionsFound: "No improvement suggestions found",
    openSection: "Open",
    apply: "Apply",
    dismiss: "Dismiss",
    allSuggestions: "All suggestions",
    improvementSuggestions: "Improvement suggestions",
    startConversation: "Start a conversation",
    askQuestion: "Ask a question or select text in the document to get help",
    improveText: "Improve text",
    writeSummary: "Write summary",
    giveSuggestions: "Give suggestions",
    use: "Use",
    insertDirectly: "Insert directly",
    withTracking: "With change tracking",
    asComment: "As comment",
    selectedText: "Selected text",
    writeMessage: "Write your message...",
    improve: "Improve",
    copied: "Copied to clipboard",
    selectTextFirst: "Select text first",
    selectTextDescription: "Select text in the document you want to improve",
    makeMorePrecise: "Make this text more precise and professional",
    writeSummaryPrompt: "Write a short summary of the document",
    giveSuggestionsPrompt: "Give improvement suggestions for this text",
    original: "Original:",
    suggested: "Suggested:"
  }
};

// Document Review Suggestions Component
const DocumentSuggestions = ({ 
  suggestions, 
  onApply, 
  onDismiss, 
  onOpenSection,
  language,
  t 
}) => {
  // Track which suggestions are being applied (for loading state)
  const [applyingIndex, setApplyingIndex] = useState(null);
  // Track which suggestions have been applied (for success state)
  const [appliedIndexes, setAppliedIndexes] = useState(new Set());
  
  if (!suggestions || suggestions.length === 0) return null;

  const handleApply = async (suggestion, type, idx) => {
    setApplyingIndex(idx);
    try {
      await onApply(suggestion, type);
      // Mark as applied
      setAppliedIndexes(prev => new Set([...prev, idx]));
      // After a short delay, collapse/remove the suggestion
      setTimeout(() => {
        onDismiss(suggestion, idx);
      }, 1500);
    } catch (error) {
      console.error('Error applying suggestion:', error);
    } finally {
      setApplyingIndex(null);
    }
  };

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-slate-500">
          {t.improvementSuggestions} ({suggestions.length})
        </span>
      </div>
      
      {suggestions.map((suggestion, idx) => {
        const isApplying = applyingIndex === idx;
        const isApplied = appliedIndexes.has(idx);
        
        return (
          <Card 
            key={suggestion.id || idx} 
            className={`overflow-hidden border-slate-200 shadow-sm transition-all duration-300 ${
              isApplied ? 'bg-emerald-50 border-emerald-200' : 'bg-white'
            }`}
          >
            {/* Header with line reference */}
            <div className={`flex items-center justify-between px-3 py-2 border-b transition-colors ${
              isApplied ? 'border-emerald-100 bg-emerald-50' : 'border-slate-100 bg-slate-50'
            }`}>
              <div className="flex items-center gap-2">
                {isApplied ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                    <Check className="w-3 h-3 mr-1" />
                    {language === 'en' ? 'Applied' : 'Brukt'}
                  </Badge>
                ) : (
                  <Badge className="bg-violet-100 text-violet-700 text-[10px] font-mono">
                    {suggestion.lineRange || `§${idx + 1}`}
                  </Badge>
                )}
                <span className="text-xs text-slate-600 truncate max-w-[150px]">
                  {suggestion.section || suggestion.title}
                </span>
              </div>
              {onOpenSection && !isApplied && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                  onClick={() => onOpenSection(suggestion)}
                >
                  {t.openSection}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
            
            {/* Content - collapsed when applied */}
            {!isApplied && (
              <div className="p-3 space-y-2">
                {/* Original text */}
                {suggestion.original && (
                  <div>
                    <p className="text-[10px] text-slate-400 mb-0.5">{t.original}</p>
                    <p className="text-xs text-slate-500 line-through">
                      {suggestion.original}
                    </p>
                  </div>
                )}
                
                {/* Suggested text */}
                <div>
                  <p className="text-[10px] text-slate-400 mb-0.5">{t.suggested}</p>
                  <p className="text-sm text-slate-800">
                    {suggestion.suggested || suggestion.recommendation}
                  </p>
                </div>
                
                {/* Rationale if available */}
                {suggestion.rationale && (
                  <p className="text-xs text-slate-500 italic pt-1 border-t border-slate-100">
                    {suggestion.rationale}
                  </p>
                )}
              </div>
            )}
            
            {/* Actions */}
            {!isApplied && (
              <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-3 text-xs text-slate-500 hover:text-slate-700"
                  onClick={() => onDismiss(suggestion, idx)}
                  disabled={isApplying}
                >
                  {t.dismiss}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="h-7 px-3 text-xs bg-slate-900 hover:bg-slate-800"
                      disabled={isApplying}
                    >
                      {isApplying ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          {language === 'en' ? 'Applying...' : 'Bruker...'}
                        </>
                      ) : (
                        <>
                          {t.apply}
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleApply(suggestion, "direct", idx)}>
                      {t.insertDirectly}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleApply(suggestion, "tracked", idx)}>
                      {t.withTracking}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export const ChatTab = ({
  chatHistory,
  onSendMessage,
  onApply,
  loading,
  selectedText,
  scope,
  language = 'nb',
  documentName = '',
  userName = '',
  onReviewDocument,
  documentSuggestions = [],
  onDismissSuggestion,
  onOpenSection
}) => {
  const [message, setMessage] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const scrollRef = useRef(null);
  
  const t = translations[language] || translations.nb;

  // Hide welcome when chat history exists
  useEffect(() => {
    if (chatHistory.length > 0 || documentSuggestions.length > 0) {
      setShowWelcome(false);
    }
  }, [chatHistory, documentSuggestions]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory, documentSuggestions]);

  const handleSend = () => {
    if (!message.trim()) return;
    setShowWelcome(false);
    onSendMessage(message, "draft");
    setMessage("");
  };

  const handleImprove = () => {
    if (!selectedText) {
      toast.error(t.selectTextFirst, { description: t.selectTextDescription });
      return;
    }
    setShowWelcome(false);
    onSendMessage(t.makeMorePrecise, "improve");
  };

  const handleReviewDocument = async () => {
    if (onReviewDocument) {
      setIsReviewing(true);
      setShowWelcome(false);
      try {
        await onReviewDocument();
      } finally {
        setIsReviewing(false);
      }
    }
  };

  const handleCopy = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success(t.copied);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApplySuggestion = (suggestion, type) => {
    onApply({
      content: suggestion.suggested || suggestion.recommendation,
      rationale: suggestion.rationale,
      original: suggestion.original,
      lineRange: suggestion.lineRange
    }, type);
  };

  // Get display name for document
  const displayDocName = documentName 
    ? documentName.replace(/\.[^/.]+$/, "").replace(/_/g, " ")
    : null;

  // Get first name from user
  const firstName = userName?.split(' ')[0] || '';

  return (
    <div className="h-full flex flex-col bg-white" data-testid="chat-tab">
      {/* Chat messages / Suggestions */}
      <div 
        ref={scrollRef} 
        className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0"
        data-testid="chat-history"
      >
        {/* Welcome Message */}
        {showWelcome && chatHistory.length === 0 && documentSuggestions.length === 0 && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            {/* AI Welcome Bubble */}
            <div className="flex justify-start mb-4">
              <div className="max-w-[90%] bg-slate-50 border border-slate-200 rounded-[20px_20px_20px_4px] px-4 py-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-800">
                      <span className="font-medium">{t.welcomeTitle}{firstName ? ` ${firstName}` : ''}! 👋</span>
                      {displayDocName ? (
                        <>
                          <br />
                          {t.welcomeWithDoc} <span className="font-medium text-violet-600">«{displayDocName}»</span>.
                        </>
                      ) : (
                        <>
                          <br />
                          {t.welcomeNoDoc}
                        </>
                      )}
                    </p>
                    
                    {/* Quick action suggestions */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300"
                        onClick={handleReviewDocument}
                        disabled={isReviewing || loading}
                      >
                        {isReviewing ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <FileSearch className="w-3.5 h-3.5" />
                        )}
                        {t.welcomeSuggestion1}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs gap-1.5 border-slate-200 hover:bg-slate-50"
                        onClick={() => {
                          setShowWelcome(false);
                          onSendMessage(language === 'nb' ? "Sjekk at alle standard juridiske klausuler er inkludert" : "Check that all standard legal clauses are included", "draft");
                        }}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        {t.welcomeSuggestion2}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Suggestions (Saga-like multi-suggestions) */}
        {documentSuggestions.length > 0 && (
          <DocumentSuggestions
            suggestions={documentSuggestions}
            onApply={handleApplySuggestion}
            onDismiss={onDismissSuggestion}
            onOpenSection={onOpenSection}
            language={language}
            t={t}
          />
        )}

        {/* Chat History */}
        {chatHistory.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            data-testid={msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}
          >
            <div
              className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-slate-900 text-white rounded-[20px_20px_4px_20px]"
                  : "bg-slate-50 border border-slate-200 text-slate-700 rounded-[20px_20px_20px_4px]"
              }`}
            >
              {/* Message content */}
              <div className="whitespace-pre-wrap">{msg.content}</div>

              {/* AI message actions */}
              {msg.role === "assistant" && msg.content && (
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-slate-200">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        size="sm" 
                        className="h-8 px-3 gap-1.5 text-xs font-medium bg-slate-900 hover:bg-slate-800"
                        data-testid="apply-button"
                      >
                        {t.use}
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => onApply(msg, "direct")}>
                        {t.insertDirectly}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onApply(msg, "tracked")}>
                        {t.withTracking}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onApply(msg, "comment")}>
                        {t.asComment}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2.5 text-slate-400 hover:text-slate-600"
                    onClick={() => handleCopy(msg.content, index)}
                    data-testid="copy-button"
                  >
                    {copiedId === index ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}

              {/* Rationale */}
              {msg.rationale && (
                <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200 italic">
                  {msg.rationale}
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {(loading || isReviewing) && (
          <div className="flex justify-start animate-in fade-in duration-200" data-testid="typing-indicator">
            <div className="bg-slate-50 border border-slate-200 rounded-[20px_20px_20px_4px] px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                  <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                  <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce"></span>
                </div>
                {isReviewing && (
                  <span className="text-xs text-slate-500 ml-2">{t.reviewingDocument}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {selectedText && (
        <div className="mx-4 mb-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-xl">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700 border-0">
              {t.selectedText}
            </Badge>
            <span className="text-xs text-slate-500 truncate flex-1">
              {selectedText.substring(0, 50)}{selectedText.length > 50 ? "..." : ""}
            </span>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="p-4 border-t border-slate-100 bg-slate-50">
        <div className="flex gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t.writeMessage}
            className="min-h-[44px] max-h-[120px] resize-none bg-white border-slate-200 focus-visible:ring-1 focus-visible:ring-violet-500 rounded-xl"
            data-testid="chat-input-textarea"
          />
          <Button
            onClick={handleSend}
            disabled={loading || !message.trim()}
            size="icon"
            className="h-11 w-11 shrink-0 bg-slate-900 hover:bg-slate-800 rounded-xl"
            data-testid="chat-send-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        
        {/* Quick action chips */}
        <div className="flex gap-2 mt-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs px-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            onClick={handleImprove}
            disabled={!selectedText}
          >
            <Wand2 className="w-3 h-3 mr-1" />
            {t.improve}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs px-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            onClick={handleReviewDocument}
            disabled={isReviewing || loading}
          >
            <FileSearch className="w-3 h-3 mr-1" />
            {t.reviewDocument}
          </Button>
        </div>
      </div>
    </div>
  );
};
export default ChatTab;
